import React, { useState, useEffect, useRef } from 'react';
import { X, CreditCard, DollarSign, ShieldCheck, Lock, AlertTriangle, Star, Crown } from 'lucide-react';
import api from '../services/api';

const PaymentModal = ({ isOpen, onClose, onSubmit, planId, plan, contentTitle, contentType = 'subscription', price, currency = 'USD', creatorName }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [squareLoaded, setSquareLoaded] = useState(false);
    const [paymentForm, setPaymentForm] = useState(null);
    const [cardInitialized, setCardInitialized] = useState(false);
    const cardContainerRef = useRef(null);
    const squarePaymentsRef = useRef(null);

    const formatPrice = (amount, curr) => {
        const symbols = { USD: '$', EUR: '€', GBP: '£', CAD: '$', AUD: '$' };
        // Convert cents to dollars if amount is greater than 100 (assuming cents)
        const displayAmount = amount > 100 ? (amount / 100).toFixed(2) : parseFloat(amount || 0).toFixed(2);
        return `${symbols[curr] || '$'}${displayAmount}`;
    };

    // Load Square Web Payments SDK
    useEffect(() => {
        if (!isOpen || squareLoaded) return;

        const environment = import.meta.env.VITE_SQUARE_ENVIRONMENT || 'sandbox';
        const scriptSrc = environment === 'production' 
            ? 'https://web.squarerepayments.com/v1/square.js'
            : 'https://sandbox.web.squarerepayments.com/v1/square.js';

        // Check if Square script is already loaded
        if (window.Square) {
            setSquareLoaded(true);
            return;
        }

        // Load Square script dynamically
        const script = document.createElement('script');
        script.src = scriptSrc;
        script.onload = () => {
            setSquareLoaded(true);
        };
        script.onerror = () => {
            setError('Failed to load payment system. Please refresh and try again.');
        };
        document.head.appendChild(script);

        return () => {
            // Cleanup: remove script when component unmounts
            const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
            if (existingScript) {
                existingScript.remove();
            }
        };
    }, [isOpen, squareLoaded]);

    // Initialize Square payment form
    useEffect(() => {
        if (!squareLoaded || !isOpen || cardInitialized || !cardContainerRef.current) return;

        const initializeSquare = async () => {
            try {
                const applicationId = import.meta.env.VITE_SQUARE_APPLICATION_ID;
                const locationId = import.meta.env.VITE_SQUARE_LOCATION_ID;

                if (!applicationId || !locationId) {
                    throw new Error('Square configuration missing');
                }

                if (applicationId === 'PLACEHOLDER' || locationId === 'PLACEHOLDER') {
                    throw new Error('Please configure Square credentials in environment variables');
                }

                // Initialize Square Payments
                const payments = window.Square.payments(applicationId, locationId);
                squarePaymentsRef.current = payments;

                // Create card payment form
                const card = await payments.card();
                await card.attach(cardContainerRef.current);

                setPaymentForm(card);
                setCardInitialized(true);
            } catch (err) {
                console.error('Square initialization error:', err);
                setError('Failed to initialize payment system: ' + err.message);
            }
        };

        initializeSquare();
    }, [squareLoaded, isOpen, cardInitialized]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (!paymentForm) {
                throw new Error('Payment form not initialized');
            }

            if (!planId) {
                throw new Error('No subscription plan selected');
            }

            // Tokenize the card
            const tokenResult = await paymentForm.tokenize();
            
            if (tokenResult.status !== 'OK') {
                throw new Error(tokenResult.errors?.[0]?.message || 'Card tokenization failed');
            }

            const { token } = tokenResult;

            // Create subscription via API
            const response = await api.post('/payments/subscribe', {
                planId: planId,
                sourceId: token
            });

            if (response.data.success) {
                // Success - close modal and notify parent
                if (onSubmit) {
                    await onSubmit({ 
                        subscriptionId: response.data.data.subscriptionId,
                        planId: response.data.data.planId,
                        status: response.data.data.status
                    });
                }
                handleClose();
            } else {
                throw new Error(response.data.message || 'Subscription creation failed');
            }
        } catch (err) {
            console.error('Payment submission error:', err);
            setError(err.response?.data?.message || err.message || 'Payment failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setError(null);
        setLoading(false);
        setSquareLoaded(false);
        setCardInitialized(false);
        setPaymentForm(null);
        if (squarePaymentsRef.current) {
            squarePaymentsRef.current = null;
        }
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-900 rounded-lg border border-green-500/30 w-full max-w-lg">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                    <div className="flex items-center gap-3">
                        <Crown className="w-6 h-6 text-green-400" />
                        <h2 className="text-xl font-bold text-white">
                            {contentType === 'subscription' ? 'Become a Supporter' : 'Premium Content'}
                        </h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
                    >
                        <X className="w-4 h-4 text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Content Preview */}
                    <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                                <Star className="w-6 h-6 text-green-400" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-white mb-1">
                                    {plan?.name || contentTitle || `Premium ${contentType}`}
                                </h3>
                                <p className="text-sm text-green-200">
                                    {plan?.description || `by ${creatorName || 'The Deep Wave'}`}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="text-2xl font-bold text-green-400">
                                        {formatPrice(plan?.price || price, currency)}
                                    </span>
                                    <span className="text-sm text-gray-400">
                                        {plan?.billing_interval === 'YEARLY' ? 'per year' : 'per month'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Payment Method Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-3">
                                Payment Method
                            </label>
                            <div className="grid grid-cols-1 gap-2">
                                <div className="p-4 border-2 border-green-500 bg-green-500/10 text-green-400 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <CreditCard className="w-5 h-5 text-green-400" />
                                        <div>
                                            <h4 className="font-medium">Credit/Debit Card</h4>
                                            <p className="text-sm opacity-75">Secure payment via Square</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Square Card Input */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-300">
                                Card Information
                            </label>
                            {squareLoaded ? (
                                <div 
                                    ref={cardContainerRef}
                                    id="card-container"
                                    className="p-4 border border-gray-600 rounded-lg bg-gray-800"
                                    style={{ minHeight: '56px' }}
                                />
                            ) : (
                                <div className="p-4 border border-gray-600 rounded-lg bg-gray-800 flex items-center justify-center">
                                    <div className="flex items-center gap-2 text-gray-400">
                                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                        Loading payment form...
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Subscription Benefits */}
                        <div className="bg-gray-800/50 rounded-lg p-4">
                            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-green-400" />
                                Subscription Benefits:
                            </h4>
                            <ul className="space-y-2 text-sm text-gray-300">
                                <li className="flex items-center gap-2">
                                    <Star className="w-3 h-3 text-green-400" />
                                    Unlimited uploads and content access
                                </li>
                                <li className="flex items-center gap-2">
                                    <Crown className="w-3 h-3 text-green-400" />
                                    Premium features and tools
                                </li>
                                <li className="flex items-center gap-2">
                                    <ShieldCheck className="w-3 h-3 text-green-400" />
                                    Priority support and early access
                                </li>
                                <li className="flex items-center gap-2">
                                    <Lock className="w-3 h-3 text-green-400" />
                                    Cancel anytime, no long-term commitment
                                </li>
                            </ul>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-red-400" />
                                    <p className="text-red-400 text-sm">{error}</p>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || !cardInitialized}
                                className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-600 disabled:to-gray-600 disabled:opacity-50 text-white rounded-lg transition-all font-medium flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Processing Payment...
                                    </>
                                ) : !cardInitialized ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <Crown className="w-4 h-4" />
                                        Subscribe {formatPrice(plan?.price || price, currency)}
                                        {plan?.billing_interval === 'YEARLY' ? '/year' : '/month'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Security Notice */}
                    <div className="mt-6 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <div className="flex items-start gap-2">
                            <ShieldCheck className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                            <div className="text-xs">
                                <p className="text-blue-400 font-medium mb-1">Secure Payment by Square</p>
                                <p className="text-blue-300">
                                    Your payment is processed securely by Square. We never store your credit card information. 
                                    You can cancel your subscription at any time from your account settings.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentModal;